import React, { useState } from "react";
import { Loader2 } from "lucide-react";
import { moveTextToFolder, CATEGORY_PATHS } from "../../services/workspaceService";
import { resolveCodeProjects } from "../../config/codeProjects";
import deskGlass from "../../styles/deskGlass";

const styles = deskGlass;

const DRAWERS = [
  { category: "código", label: "Código" },
  { category: "iglesia", label: "Iglesia" },
  { category: "vida", label: "Vida" },
  { category: "cuentos", label: "Cuentos" },
];

function notebooksFor(category, settings) {
  const path = CATEGORY_PATHS[category];
  if (!path) return [];
  if (category === "código") {
    return [
      { notebookName: path.notebookName, label: "Notas", color: "#94a3b8" },
      ...resolveCodeProjects(settings).map((p) => ({
        notebookName: p.notebookName,
        label: p.label,
        color: p.color,
      })),
    ];
  }
  return [{ notebookName: path.notebookName, label: path.notebookName || "Notas", color: "#D4AF37" }];
}

function drawerFromFolder(drawerName) {
  return Object.entries(CATEGORY_PATHS).find(([, p]) => p.drawerName === drawerName)?.[0] || null;
}

/**
 * One-tap move to cajón / cuaderno after save or from reader / catch-up.
 * Tap a category to expand its notebooks; tap a notebook to move.
 */
export default function QuickMoveBar({ location, settings, onMoved, compact = false }) {
  const [moving, setMoving] = useState(null);
  const [error, setError] = useState(null);
  const [ok, setOk] = useState(null);
  const [openCategory, setOpenCategory] = useState(null);

  if (!location?.textId || !location?.deskId) return null;

  const chipStyle = (active, color) => ({
    ...styles.viewToggle,
    fontSize: compact ? "0.7rem" : "0.75rem",
    padding: compact ? "3px 8px" : "4px 10px",
    ...(active ? styles.viewToggleActive : {}),
    ...(active && color ? { borderColor: color } : {}),
    opacity: moving ? 0.6 : 1,
    cursor: moving ? "wait" : "pointer",
  });

  const handleMove = async (category, notebookName = null) => {
    const key = `${category}:${notebookName || "default"}`;
    if (moving) return;
    setMoving(key);
    setError(null);
    setOk(null);
    try {
      const { receipt, unchanged } = await moveTextToFolder(location, category, { settings, notebookName });
      if (!unchanged) {
        const dest = notebookName
          ? `${CATEGORY_PATHS[category].drawerName} › ${notebookName}`
          : CATEGORY_PATHS[category].drawerName;
        setOk(dest);
        onMoved?.(receipt);
      }
    } catch (err) {
      console.error("[QuickMoveBar]", err);
      setError(err.message || "No se pudo mover");
    } finally {
      setMoving(null);
    }
  };

  const currentDrawer = location.path?.split(" › ")[1] || null;
  const currentNotebook = location.path?.split(" › ")[2] || null;
  const currentCategory = drawerFromFolder(currentDrawer);
  const notebooks = openCategory ? notebooksFor(openCategory, settings) : [];

  return (
    <div style={{ marginTop: compact ? "8px" : "10px" }}>
      <div style={{ ...styles.subtext, fontSize: compact ? "0.7rem" : "0.75rem", marginBottom: "6px", color: "rgba(255,255,255,0.55)" }}>
        Guardar en
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
        {DRAWERS.map(({ category, label }) => {
          const active = openCategory === category || (!openCategory && currentCategory === category);
          return (
            <button
              key={category}
              type="button"
              disabled={!!moving}
              style={chipStyle(active)}
              onClick={() => setOpenCategory((c) => (c === category ? null : category))}
              title={`Elegir cuaderno en ${CATEGORY_PATHS[category].drawerName}`}
            >
              {label}
            </button>
          );
        })}
      </div>

      {openCategory && notebooks.length > 0 && (
        <>
          <div style={{ ...styles.subtext, fontSize: compact ? "0.7rem" : "0.75rem", margin: "8px 0 6px", color: "rgba(255,255,255,0.55)" }}>
            {CATEGORY_PATHS[openCategory].drawerName} › cuaderno
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
            {notebooks.map(({ notebookName, label, color }) => {
              const active =
                currentDrawer === CATEGORY_PATHS[openCategory].drawerName &&
                currentNotebook === notebookName;
              return (
                <button
                  key={notebookName}
                  type="button"
                  disabled={!!moving}
                  style={chipStyle(active, color)}
                  onClick={() => handleMove(openCategory, notebookName)}
                  title={`Temas › ${CATEGORY_PATHS[openCategory].drawerName} › ${notebookName}`}
                >
                  {moving === `${openCategory}:${notebookName}` ? (
                    <Loader2 size={12} className="animate-spin" />
                  ) : (
                    label
                  )}
                </button>
              );
            })}
          </div>
        </>
      )}

      {ok && (
        <div style={{ color: "#4ade80", fontSize: "0.75rem", marginTop: "8px" }}>
          ✓ Movido a {ok}
        </div>
      )}
      {error && (
        <div style={{ color: "#ffb4b4", fontSize: "0.75rem", marginTop: "8px" }} role="alert">
          {error}
        </div>
      )}
    </div>
  );
}
